import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    IconAsset,
    SafeLuminary,
    Scene,
    Subscene,
    TextAsset,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { ChartAsset } from '@lumen5/luminary/core/asset/ChartAsset';

import type { TransitionedAssetError } from 'js/src/client';
import { SlideBackgroundModelTypeEnum, TransitionedAssetErrorTypeEnum } from 'js/src/client';
import { DEPRECATED_INVISIBLE_URL, OUTRO_DUMMY_URL } from 'js/src/libs/consts';

/*
 * Returns all editable media assets defined on the scene, which includes
 * subscenes and asset groups
 */
export function getEditableMediaAssets(scene: Readonly<Scene>) {
    return scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).filter((asset) => asset.isEditable);
}

/*
 * Returns all editable media assets in the video, returning the source & source_id for each
 */
export function getVideoEditableMediaAssetSourceData(
    luminary: Readonly<Luminary>,
): [number, string][] {
    return luminary.scenes
        .flatMap((scene) => scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE))
        .filter((asset) => asset.isEditable)
        .filter((asset) => asset.sourceData.source !== null && asset.sourceData.source_id !== null)
        .map((asset) => [asset.sourceData.source, asset.sourceData.source_id]);
}

/**
 * Returns assets defined on the scene, not the subscene. This includes assets
 * that are in asset groups defined on the scene.
 * NOTE: can't just recurse since that'll pickup subscene assets as well!
 */
export function getEditableAssetsOnSceneLevel(
    scene: Scene,
): (TimeAwareMediaAsset | TextAsset | IconAsset | ChartAsset)[] {
    const sceneLevelAssetGroups = scene.getAssetsByType(VideoData.GROUP_ASSET_TYPE, {
        deep: false,
    });
    const editableAssetFilter = (
        asset: Asset,
    ): asset is TimeAwareMediaAsset | IconAsset | TextAsset | ChartAsset =>
        (VideoData.isMediaAsset(asset) ||
            VideoData.isIconAsset(asset) ||
            VideoData.isTextAsset(asset) ||
            VideoData.isChartAsset(asset)) &&
        asset.isEditable;

    return [
        // standalone assets
        ...scene.getAssets({ deep: false }),
        // assets in scene-level groups
        ...sceneLevelAssetGroups.flatMap((group) => group.getAssets()),
    ].filter(editableAssetFilter);
}

/**
 * Returns assets defined on subscenes, not the scene. This includes assets
 * that are in asset groups defined on subscenes.
 */
export function getEditableAssetsOnSubsceneLevel(
    subscene: Subscene,
): (TimeAwareMediaAsset | TextAsset | IconAsset | ChartAsset)[] {
    return [
        ...(subscene.getAssets().filter((asset) => asset.isEditable) as (
            | TimeAwareMediaAsset
            | TextAsset
            | IconAsset
            | ChartAsset
        )[]),
    ];
}

const _getLargestAssetFromList = (assetList: TimeAwareMediaAsset[]) =>
    assetList.reduce((largest: TimeAwareMediaAsset | null, current) => {
        if (!largest) {
            return current;
        }

        if (!current) {
            return null;
        }

        if (current.height * current.width > largest.height * largest.width) {
            return current;
        }
        return largest;
    }, null);

/*
 * If the scene has at least one editable media asset, returns the
 * asset with the largest possible bounds. This will typically (but not always)
 * correspond to an asset used as full bleed background
 */
export function getLargestMediaAsset(
    scene: Scene,
    requireEditable = false,
): TimeAwareMediaAsset | null {
    const mediaAssetChildren = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
    if (!mediaAssetChildren.length) {
        return null;
    }

    if (!requireEditable) {
        return _getLargestAssetFromList(mediaAssetChildren);
    }

    const editableAssets = mediaAssetChildren.filter((asset: Asset) => asset.isEditable);
    if (!editableAssets.length) {
        return null;
    }

    return _getLargestAssetFromList(editableAssets);
}

/*
 * Returns our best guess of which editable media asset on the scene the user would like to replace.
 */
export function getDefaultMediaToReplace(scene: Scene): TimeAwareMediaAsset | null {
    let mediaAssetChildren = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
    if (!mediaAssetChildren.length) {
        return null;
    }

    if (scene.sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT) {
        // filter out the talking head media asset, we don't want to change that!
        mediaAssetChildren = mediaAssetChildren.filter((asset) => !asset.linkToSceneGroup);
    }

    return _getLargestAssetFromList(mediaAssetChildren.filter((asset) => asset.isEditable));
}

/**
 * True if the media asset has source data that is not empty or invisible
 */
export function isMediaAssetEmpty(mediaAsset: TimeAwareMediaAsset): boolean {
    const uuidList = [
        VideoData.INVISIBLE_IMAGE_SOURCE_DATA.uuid,
        VideoData.BLACK_IMAGE_SOURCE_DATA.uuid,
    ];
    const thumbnailUrlList = [
        DEPRECATED_INVISIBLE_URL,
        OUTRO_DUMMY_URL,
        'https://storage.googleapis.com/lumen5-prod-images/logo-placeholder_256.png', // outro placeholder logo
        'https://storage.googleapis.com/lumen5-user-dc8fdb8e-6c6e-4cfa-89c2-25d6ab0552ba/tmppqvm4vqg.png', // custom outro (fullscreen) placeholder image
    ];
    return (
        uuidList.includes(mediaAsset.sourceData.uuid) ||
        thumbnailUrlList.includes(mediaAsset.thumbnailUrl as string)
    );
}

/**
 * True if the media asset target resolution is equal to or greater than the full scene
 */
export function isMediaAssetFullBleed(
    luminary: SafeLuminary,
    mediaAsset: TimeAwareMediaAsset,
): boolean {
    return (
        mediaAsset.mediaTargetResolution.height >= luminary.referenceResolution.height &&
        mediaAsset.mediaTargetResolution.width >= luminary.referenceResolution.width
    );
}

/*
 * Iterates through all scenes and returns a list of (source, source_id) pairs for all background media assets
 */
export function getBackgroundMediaAssetSourceData(
    luminary: SafeLuminary,
): TransitionedAssetError[] {
    const results: TransitionedAssetError[] = [];
    luminary.scenes.forEach((scene) => {
        scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).forEach((asset) => {
            const { source, source_id } = asset.sourceData;
            if (source === null || source_id === null) {
                return;
            }
            const sourceData = asset.sourceData;
            if (sourceData.type === SlideBackgroundModelTypeEnum.VIDEO) {
                results.push({
                    sceneId: scene.id,
                    assetName: asset.name,
                    type: sourceData.type as unknown as TransitionedAssetErrorTypeEnum,
                    [sourceData.type]: {
                        source,
                        source_id,
                    },
                    image: null,
                    icon: null,
                });
            } else if (sourceData.type === SlideBackgroundModelTypeEnum.IMAGE) {
                results.push({
                    sceneId: scene.id,
                    assetName: asset.name,
                    type: sourceData.type as unknown as TransitionedAssetErrorTypeEnum,
                    [sourceData.type]: {
                        source,
                        source_id,
                    },
                    video: null,
                    icon: null,
                });
            }
        });
        scene.getAssetsByType(VideoData.ICON_ASSET_TYPE).forEach((asset) => {
            const { source, source_id } = asset.sourceData;
            if (source === null || source_id === null) {
                return;
            }
            results.push({
                sceneId: scene.id,
                assetName: asset.name,
                type: TransitionedAssetErrorTypeEnum.ICON,
                icon: {
                    source,
                    source_id,
                },
                video: null,
                image: null,
            });
        });
    });
    return results;
}
